import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { AdminUserRow, Plan, PaymentRow, PromoRow } from '../types';
import toast from 'react-hot-toast';

const formatRub = (kopecks: number): string =>
  (kopecks / 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU');
};

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [promos, setPromos] = useState<PromoRow[]>([]);
  const [grantSel, setGrantSel] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: '', duration_days: 30, max_uses: '' });

  const loadAll = useCallback(async () => {
    const [u, pl, pay, pr] = await Promise.all([
      apiService.adminListUsers(),
      apiService.getPlans(),
      apiService.adminListPayments(),
      apiService.adminListPromo(),
    ]);
    setUsers(u);
    setPlans(pl);
    setPayments(pay);
    setPromos(pr);
  }, []);

  useEffect(() => {
    (async () => {
      if (!apiService.isAuthenticated()) {
        navigate('/login');
        return;
      }
      try {
        const me = await apiService.getMe();
        if (!me.is_admin) {
          toast.error('Доступ только для администратора');
          navigate('/dashboard');
          return;
        }
        setChecked(true);
        await loadAll();
      } catch {
        navigate('/dashboard');
      }
    })();
  }, [navigate, loadAll]);

  const doGrant = async (userId: number) => {
    const code = grantSel[userId];
    if (!code) {
      toast.error('Выберите тариф');
      return;
    }
    try {
      setBusy(true);
      await apiService.adminGrant(userId, { plan_code: code, source: 'manual' });
      toast.success('Подписка выдана');
      await loadAll();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const doGrace = async () => {
    if (!window.confirm('Выдать бесплатный доступ ВСЕМ, у кого нет активной подписки?\n\nВнимание: сейчас включён платный доступ, поэтому это сделает бесплатными и новых пользователей. Обычно повторно нажимать не нужно.')) return;
    try {
      setBusy(true);
      const r = await apiService.adminGrantGrace();
      toast.success(`Грейс выдан: ${r.granted} польз. на ${r.duration_days} дн.`);
      await loadAll();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const toggleAdmin = async (u: AdminUserRow) => {
    try {
      setBusy(true);
      await apiService.adminSetAdmin(u.id, !u.is_admin);
      await loadAll();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const genCode = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 6; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
    setNewPromo({ ...newPromo, code: 'IISHET-' + s });
  };

  const doCreatePromo = async () => {
    const code = newPromo.code.trim();
    if (!code) { toast.error('Введите код'); return; }
    try {
      setBusy(true);
      await apiService.adminCreatePromo({
        code,
        duration_days: Number(newPromo.duration_days) || 30,
        max_uses: newPromo.max_uses ? Number(newPromo.max_uses) : undefined,
      });
      toast.success('Промокод создан');
      setNewPromo({ code: '', duration_days: 30, max_uses: '' });
      await loadAll();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  if (!checked) return <div className="p-8 text-gray-400">Проверка доступа…</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Админ-панель</h1>
            <p className="text-xs text-gray-400 mt-0.5">Пользователи, доступы, промокоды и платежи. Раздел виден только администраторам.</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={doGrace} disabled={busy}
              title="Даёт бесплатный доступ всем, у кого нет активной подписки"
              className="text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-3 py-1.5 disabled:opacity-50">
              Выдать грейс всем
            </button>
            <button onClick={() => navigate('/dashboard')}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
              ← В кабинет
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        {/* Пользователи */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Пользователи ({users.length})
          </h2>
          <p className="text-xs text-gray-400 mb-3">
            Все зарегистрированные. «Подписка» — активный доступ и до какой даты. «Выдать тариф» —
            дать доступ вручную. Галочка «Админ» — права администратора (доступ к этой панели).
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Подписка</th>
                  <th className="px-3 py-2 text-left">До</th>
                  <th className="px-3 py-2 text-left">Заявки</th>
                  <th className="px-3 py-2 text-left">Выдать тариф</th>
                  <th className="px-3 py-2 text-left">Админ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => {
                  const s = u.subscription;
                  return (
                    <tr key={u.id}>
                      <td className="px-3 py-2">{u.id}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{u.email}</td>
                      <td className="px-3 py-2">
                        {s && s.active ? (
                          <span className="inline-block px-2 py-0.5 rounded bg-green-100 text-green-800">
                            {s.plan_name || s.source || 'активна'}
                          </span>
                        ) : (
                          <span className="text-gray-400">нет</span>
                        )}
                      </td>
                      <td className="px-3 py-2">{s && s.active ? formatDate(s.expires_at) : '—'}</td>
                      <td className="px-3 py-2">
                        {s && s.active
                          ? s.request_quota == null
                            ? `${s.requests_used} / ∞`
                            : `${s.requests_used} / ${s.request_quota}`
                          : u.total_requests}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <select
                            value={grantSel[u.id] || ''}
                            onChange={(e) => setGrantSel({ ...grantSel, [u.id]: e.target.value })}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                          >
                            <option value="">—</option>
                            {plans.map((p) => (
                              <option key={p.code} value={p.code}>{p.name}</option>
                            ))}
                          </select>
                          <button onClick={() => doGrant(u.id)} disabled={busy}
                            className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                            выдать
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={u.is_admin} disabled={busy}
                          onChange={() => toggleAdmin(u)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Промокоды */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Промокоды ({promos.length})
          </h2>
          <p className="text-xs text-gray-400 mb-3">
            Дают бесплатный доступ без оплаты. Создайте код (или сгенерируйте), задайте срок в днях
            и, при желании, число активаций — и отдайте пользователю. Он вводит код в личном кабинете
            или на странице тарифов.
          </p>
          <div className="rounded-lg border border-gray-200 bg-white p-4 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Код</label>
                <div className="flex gap-1">
                  <input value={newPromo.code}
                    onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value })}
                    placeholder="IISHET-XXXXXX"
                    className="border border-gray-300 rounded px-2 py-1 text-sm uppercase w-44" />
                  <button onClick={genCode} type="button"
                    className="text-xs text-indigo-600 hover:text-indigo-800 px-2">сгенерировать</button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Дней доступа</label>
                <input type="number" value={newPromo.duration_days}
                  onChange={(e) => setNewPromo({ ...newPromo, duration_days: Number(e.target.value) })}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-24" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Макс. активаций</label>
                <input type="number" value={newPromo.max_uses} placeholder="без лимита"
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-28" />
              </div>
              <button onClick={doCreatePromo} disabled={busy}
                className="text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-md px-4 py-2 disabled:opacity-50">
                Создать
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              Пусто в «Макс. активаций» — без ограничения по числу. Один код активируется пользователем один раз.
            </p>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Код</th>
                  <th className="px-3 py-2 text-left">Дней</th>
                  <th className="px-3 py-2 text-left">Активаций</th>
                  <th className="px-3 py-2 text-left">Статус</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {promos.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-4 text-gray-400">Промокодов пока нет</td></tr>
                )}
                {promos.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2 font-mono text-gray-900">{p.code}</td>
                    <td className="px-3 py-2">{p.duration_days}</td>
                    <td className="px-3 py-2">{p.used_count}{p.max_uses != null ? ` / ${p.max_uses}` : ''}</td>
                    <td className="px-3 py-2">{p.is_active ? 'активен' : 'выключен'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Платежи */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Платежи ({payments.length})
          </h2>
          <p className="text-xs text-gray-400 mb-3">
            Оплаты через Т-Кассу. Статус «confirmed» — оплата прошла, «rejected» — отклонена,
            «refunded» — возврат, «new» — платёж создан, но ещё не оплачен.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Сумма</th>
                  <th className="px-3 py-2 text-left">Статус</th>
                  <th className="px-3 py-2 text-left">OrderId</th>
                  <th className="px-3 py-2 text-left">Дата</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-4 text-gray-400">Платежей пока нет</td></tr>
                )}
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2">{p.id}</td>
                    <td className="px-3 py-2">{p.user_id}</td>
                    <td className="px-3 py-2">{formatRub(p.amount_kopecks)}</td>
                    <td className="px-3 py-2">{p.status}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.order_id}</td>
                    <td className="px-3 py-2">{formatDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Admin;
